"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Phone, Mail, X, Instagram, Facebook, ThumbsUp, MapPin, Navigation } from "lucide-react"
import { trackEvent } from "@/lib/analytics"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from "@/components/ui/drawer"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { VisualSiloContent } from "@/components/visualsilo-content"

export function Footer() {
  const [isVisualSiloOpen, setIsVisualSiloOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isReviewMenuOpen, setIsReviewMenuOpen] = useState(false)

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 1024) // lg breakpoint
    }
    
    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    
    return () => {
      window.removeEventListener('resize', checkIsMobile)
    }
  }, [])

  return (
    <footer className="bg-[#231f20] text-white py-16">
      <div className="container mx-auto px-4 md:px-0">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Column 1: Logo and Description */}
          <div className="md:col-span-4">
            <div className="mb-6">
              <img
                src="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/logo/RT-LOGO-Footer.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvbG9nby9SVC1MT0dPLUZvb3Rlci5wbmciLCJpYXQiOjE3Njc2ODQwNzEsImV4cCI6MjA4MzA0NDA3MX0.4UZQxuCIdaNj07nYkT8JI_IuxklPPeIUGdISobn5PQs"
                alt="Raintree Chinese Medicine and Acupuncture"
                className="h-auto w-4/5 md:w-full mx-auto justify-center align-center object-contain"
              />
            </div>
          </div>

          {/* Column 2-3: Two columns for links and contact info */}
          <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Column 1: Quick Links and What We Help With */}
            <div className="space-y-8">
              {/* Quick Links */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/clinics"
                      className="hover:text-[#DBB981] transition-colors text-left"
                    >
                      Clinics
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/what-we-help-with"
                      className="hover:text-[#DBB981] transition-colors text-left"
                    >
                      What We Help With
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/dr-renee-knott"
                      className="hover:text-[#DBB981] transition-colors text-left"
                    >
                      Dr Renee Knott
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/about"
                      className="hover:text-[#DBB981] transition-colors text-left"
                    >
                      About Raintree
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/appointments"
                      className="hover:text-[#DBB981] transition-colors text-left"
                    >
                      Appointments
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/contact"
                      className="hover:text-[#DBB981] transition-colors text-left"
                    >
                      Contact
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/faq"
                      className="hover:text-[#DBB981] transition-colors text-left"
                    >
                      FAQ
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/appointments#faq-fees-section"
                      className="hover:text-[#DBB981] transition-colors text-left"
                    >
                      HICAPS
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/privacy"
                      className="hover:text-[#DBB981] transition-colors text-left"
                    >
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/terms"
                      className="hover:text-[#DBB981] transition-colors text-left"
                    >
                      Terms & Conditions
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/blog"
                      className="hover:text-[#DBB981] transition-colors text-left"
                    >
                      Blog
                    </Link>
                  </li>
                </ul>
              </div>

              {/* What We Help With */}
              <div>
                <h3 className="text-lg font-semibold mb-4">What We Help With</h3>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/what-we-help-with/fertility-preconception"
                      className="hover:text-[#DBB981] transition-colors"
                    >
                      Preconception Care & Fertility Support
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/what-we-help-with/pregnancy-postpartum"
                      className="hover:text-[#DBB981] transition-colors"
                    >
                      Pregnancy & Postpartum Support
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/what-we-help-with/menstrual-hormonal-health"
                      className="hover:text-[#DBB981] transition-colors"
                    >
                      Menstrual & Hormonal Health
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/what-we-help-with/stress-sleep-digestion"
                      className="hover:text-[#DBB981] transition-colors"
                    >
                      Stress, Sleep & Digestive Balance
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/what-we-help-with/womens-health-long-term-care"
                      className="hover:text-[#DBB981] transition-colors"
                    >
                      Ongoing Women's Health & Long-Term Care
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/what-we-help-with/other-concerns"
                      className="hover:text-[#DBB981] transition-colors"
                    >
                      Other Concerns
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            {/* Column 2: Social Links, Contact, and Clinic Locations */}
            <div className="space-y-8">
              {/* Social Links */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Social Links</h3>
                <ul className="space-y-2">
                  <li className="relative">
                    <DropdownMenu open={isReviewMenuOpen} onOpenChange={setIsReviewMenuOpen}>
                      <DropdownMenuTrigger
                        onMouseEnter={() => setIsReviewMenuOpen(true)}
                        onMouseLeave={() => setIsReviewMenuOpen(false)}
                        className={`transition-colors text-left flex items-center gap-2 w-full cursor-pointer ${
                          isReviewMenuOpen ? 'text-[#DBB981]' : 'hover:text-[#DBB981]'
                        }`}
                      >
                        <ThumbsUp className="h-4 w-4 flex-shrink-0" />
                        Write a Google review
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        sideOffset={0}
                        onMouseEnter={() => setIsReviewMenuOpen(true)}
                        onMouseLeave={() => setIsReviewMenuOpen(false)}
                        className="bg-[#231f20] border-gray-600 text-white mt-0"
                      >
                        <DropdownMenuItem
                          asChild
                          className="hover:bg-white hover:text-[#231f20] focus:bg-white focus:text-[#231f20] cursor-pointer transition-colors"
                        >
                          <a
                            href="https://g.page/r/CUQcdB8bKkFcEBM/review"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2"
                          >
                            <MapPin className="h-4 w-4" />
                            Raintree Noosa
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          asChild
                          className="hover:bg-white hover:text-[#231f20] focus:bg-white focus:text-[#231f20] cursor-pointer transition-colors"
                        >
                          <a
                            href="https://g.page/r/CUQcdB8bKkFcEBM/review"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2"
                          >
                            <MapPin className="h-4 w-4" />
                            Raintree Maroochydore
                          </a>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </li>
                  <li>
                    <a
                      href="https://www.instagram.com/raintreechinesemedicine/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-[#DBB981] transition-colors text-left flex items-center gap-2"
                    >
                      <Instagram className="h-4 w-4 flex-shrink-0" />
                      Instagram
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.facebook.com/Raintreechinesemedicine"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-[#DBB981] transition-colors text-left flex items-center gap-2"
                    >
                      <Facebook className="h-4 w-4 flex-shrink-0" />
                      Facebook
                    </a>
                  </li>
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Contact</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 flex-shrink-0" />
                    <a href="tel:+61752409192" onClick={() => trackEvent('call_click', { location: 'footer' })}>+61 7 5240 9192</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 flex-shrink-0" />
                    <a href="mailto:admin@raintreechinesemedicine.com.au" className="hover:underline">
                      admin@raintreechinesemedicine.com.au
                    </a>
                  </div>
                </div>
              </div>

              {/* Clinic Locations */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Clinic Locations</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-base font-medium mb-1 flex items-center gap-2">
                      <MapPin className="h-4 w-4 flex-shrink-0" />
                      Noosa
                    </h4>
                    <p className="text-sm text-gray-300 mb-1">Open Mon, Wed & Fri</p>
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=31+Burgess+Drive,+Tewantin+QLD+4565"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-300 hover:text-[#DBB981] transition-colors block mb-2"
                    >
                      31 Burgess Drive, Tewantin QLD 4565
                    </a>
                    <a
                      href="https://www.google.com/maps/dir/?api=1&destination=31+Burgess+Drive,+Tewantin+QLD+4565"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#DBB981] hover:text-[#DBB981]/80 transition-colors flex items-center gap-2"
                    >
                      <Navigation className="h-4 w-4 flex-shrink-0" />
                      Get Directions
                    </a>
                  </div>
                  <div>
                    <h4 className="text-base font-medium mb-1 flex items-center gap-2">
                      <MapPin className="h-4 w-4 flex-shrink-0" />
                      Maroochydore
                    </h4>
                    <p className="text-sm text-gray-300 mb-1">Open Tue and Thu</p>
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=Ground+Level+5/55+Plaza+Parade,+Maroochydore+QLD+4558"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-300 hover:text-[#DBB981] transition-colors block mb-2"
                    >
                      Ground Level 5/55 Plaza Parade, Maroochydore QLD 4558
                    </a>
                    <a
                      href="https://www.google.com/maps/dir/?api=1&destination=Ground+Level+5/55+Plaza+Parade,+Maroochydore+QLD+4558"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#DBB981] hover:text-[#DBB981]/80 transition-colors flex items-center gap-2"
                    >
                      <Navigation className="h-4 w-4 flex-shrink-0" />
                      Get Directions
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-600 mt-12 pt-8 text-center">
          <p className="text-gray-300">
            © {new Date().getFullYear()} Raintree Chinese Medicine and Acupuncture. All rights reserved.
          </p>
          <p className="text-gray-300 mt-2 text-sm">
            Site by{" "}
            <button
              onClick={() => setIsVisualSiloOpen(true)}
              className="font-semibold hover:text-[#DBB981] transition-colors cursor-pointer"
            >
              VisualSilo
            </button>
            {" "}|{" "}
            <a
              href="https://clinic.raintreechinesemedicine.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-[#DBB981] transition-colors"
            >
              Admin
            </a>
          </p>
        </div>
      </div>

      {/* VisualSilo Modal/Drawer */}
      {isMobile ? (
        <Drawer open={isVisualSiloOpen} onOpenChange={setIsVisualSiloOpen}>
          <DrawerContent className="h-[90vh] flex flex-col">
            <DrawerTitle className="sr-only">VisualSilo</DrawerTitle>
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">VisualSilo</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsVisualSiloOpen(false)}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <VisualSiloContent />
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={isVisualSiloOpen} onOpenChange={setIsVisualSiloOpen}>
          <DialogContent className="max-w-2xl w-full max-h-[80vh] p-0 rounded-lg overflow-hidden flex flex-col">
            <DialogHeader className="p-4 border-b">
              <DialogTitle>VisualSilo</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-auto p-4">
              <VisualSiloContent />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </footer>
  )
}